/* eslint-disable array-callback-return */
import { Fragment, useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import HeaderTitle from '../../components/HeaderTitle'
import LoadingSpinner from '../../components/LoadingSpinner'
import ConfirmationDialog from '../../components/ConfirmationDialog'
import { setToastMessage } from '../../reducers/common.reducer'
import eventMatchService from '../../services/eventMatchService'
import { get555Event, get909Event } from './eventMatchHelper'
import { EventSelect } from './components'
import { findAutoMatches } from './eventMatching'

const STATUS_OPTIONS = [
  { id: 'all', name: 'All events' },
  { id: 'pending', name: 'Pending' },
  { id: 'matched', name: 'Matched' },
]

const DEFAULT_FILTERS = {
  home: '',
  away: '',
  league: '',
}

function MatchEvent() {
  const dispatch = useDispatch()
  const [event909, setEvent909] = useState({ eventDetails: [] })
  const [isLoading, setIsLoading] = useState(false)
  const [event555, setEvent555] = useState([])
  const [count555, setCount555] = useState(0)
  const [count909, setCount909] = useState(0)
  const [matchedEvent, setMatchedEvent] = useState([])
  const [selectedEventList, setSelectedEventList] = useState([])
  const [statusFilter, setStatusFilter] = useState(STATUS_OPTIONS[0])
  const [searchFilters, setSearchFilters] = useState(() => ({ ...DEFAULT_FILTERS }))
  const [isAutoMatching, setIsAutoMatching] = useState(false)
  const [isSaveConfirmOpen, setIsSaveConfirmOpen] = useState(false)

  useEffect(() => {
    getEvent()
  }, [])

  const getEvent = async () => {
    try {
      setIsLoading(true)
      const results = await Promise.all([get909Event(), get555Event()])

      const allRequestsSuccessful = results.every((result) => result.status === 200)

      if (allRequestsSuccessful) {
        const temp909 = results[0].data ?? { eventDetails: [] }
        const temp555 = results[1].data ?? { matches: [] }
        const eventMatches = await eventMatchService.getEventMatch()

        setMatchedEvent(eventMatches.data?.data ?? [])
        setCount555(temp555.matches?.length ?? 0)
        setCount909(temp909.eventDetails?.length ?? 0)

        const transformedArray = (temp555.matches ?? []).map((item) => {
          const homeName = item.home?.engName ?? item.home?.name ?? 'Home'
          const awayName = item.away?.engName ?? item.away?.name ?? 'Away'
          return {
            id: item.matchId?.toString(),
            label: `${homeName} vs ${awayName}`,
            description: item.league?.engName ?? item.leagueName ?? '',
            homeName,
            awayName,
            kickoff: item.eventTime ?? item.startTime ?? item.matchTime ?? item.kickoff,
          }
        })

        setEvent909(temp909)
        setEvent555(transformedArray)
      }
    } catch (error) {
      console.log({ error })
      dispatch(
        setToastMessage({
          type: 'error',
          message: error?.response?.data?.message ?? 'Failed to load match events',
        }),
      )
    } finally {
      setIsLoading(false)
    }
  }

  const handleAutoMatch = () => {
    const existingTargetIds = new Set(
      matchedEvent
        .map((item) => item?.eventId555)
        .filter(Boolean)
        .map((id) => String(id)),
    )
    const pendingSourceIds = new Set(selectedEventList.map((item) => String(item.eventId909)))
    const pendingTargetIds = new Set(
      selectedEventList
        .map((item) => item.eventId555)
        .filter(Boolean)
        .map((id) => String(id)),
    )

    const sourceEvents = (event909.eventDetails ?? [])
      .filter((event) => !matchedLookup.has(String(event?.rapidEventId)))
      .filter((event) => !pendingSourceIds.has(String(event?.rapidEventId)))
      .map((event) => ({
        id: event?.rapidEventId?.toString(),
        homeName: event?.homeTeam,
        awayName: event?.awayTeam,
        kickoff: event?.eventTime,
      }))

    const availableTargetEvents = event555.filter(
      (event) => !pendingTargetIds.has(String(event.id)),
    )
    setIsAutoMatching(true)

    try {
      const result = findAutoMatches(
        sourceEvents,
        availableTargetEvents,
        existingTargetIds,
      )
      setSelectedEventList((current) => [
        ...current,
        ...result.matches.map(({ sourceEvent, targetEvent }) => ({
          eventId909: String(sourceEvent.id),
          eventId555: String(targetEvent.id),
        })),
      ])
      dispatch(
        setToastMessage({
          type: result.matches.length ? 'success' : 'info',
          message: result.matches.length
            ? `${result.matches.length} direct Home/Away matches ready to review. ${result.skippedReversed} reversed match(es) skipped.`
            : `No safe direct matches found. ${result.skippedReversed} reversed match(es) skipped.`,
        }),
      )
    } finally {
      setIsAutoMatching(false)
    }
  }

  const handleChange = async (event, newValue, rapidId) => {
    const eventId909 = rapidId?.toString()

    if (!eventId909) {
      return
    }

    if (!newValue?.id) {
      setSelectedEventList((prev) => prev.filter((value) => value.eventId909 !== eventId909))
      return
    }

    const matchedEventId = matchedLookup.get(eventId909)?.eventId555?.toString()
    const incomingEventId = newValue.id.toString()

    if (matchedEventId && matchedEventId === incomingEventId) {
      setSelectedEventList((prev) => prev.filter((value) => value.eventId909 !== eventId909))
      return
    }

    setSelectedEventList((prev) => {
      const existing = prev.find((value) => value.eventId909 === eventId909)
      if (existing) {
        return prev.map((value) =>
          value.eventId909 === eventId909
            ? { ...value, eventId555: incomingEventId }
            : value,
        )
      }
      return [...prev, { eventId555: incomingEventId, eventId909 }]
    })
  }

  const handleSave = () => {
    if (!selectedEventList.length) {
      dispatch(setToastMessage({ type: 'error', message: 'Please select event!' }))
      return
    }

    setIsSaveConfirmOpen(true)
  }

  const handleSaveConfirmed = async () => {
    setIsSaveConfirmOpen(false)

    try {
      setIsLoading(true)
      const finalData = selectedEventList.filter((value) => value.eventId555)
      //console.log("finalData", finalData)
      if (!finalData.length) {
        dispatch(setToastMessage({ type: 'error', message: 'Please select event!' }))
        return
      }

      const res = await eventMatchService.saveEventMatchArray(finalData)
      await getEvent()
      setSelectedEventList([])

      if (res.data?.success) {
        dispatch(setToastMessage({ type: 'success', message: 'Successfully inserted' }))
      } else {
        dispatch(
          setToastMessage({
            type: 'error',
            message: res.data?.message ?? 'Unable to save event matches',
          }),
        )
      }
    } catch (error) {
      dispatch(
        setToastMessage({
          type: 'error',
          message: error.response ? error.response.data.error : error.message,
        }),
      )
      await getEvent()
      setSelectedEventList([])
    } finally {
      setIsLoading(false)
    }
  }

  const handleReset = async () => {
    setSelectedEventList([])
    setSearchFilters({ ...DEFAULT_FILTERS })
    setStatusFilter(STATUS_OPTIONS[0])
    //await getEvent()
  }

  const matchedLookup = useMemo(() => {
    const lookup = new Map()
    matchedEvent.forEach((item) => {
      if (item?.eventId909) {
        lookup.set(item.eventId909.toString(), item)
      }
    })
    return lookup
  }, [matchedEvent])

  const pendingLookup = useMemo(() => {
    const lookup = new Map()
    selectedEventList.forEach((item) => {
      if (item?.eventId909) {
        lookup.set(item.eventId909.toString(), item.eventId555?.toString() ?? null)
      }
    })
    return lookup
  }, [selectedEventList])

  const eventDetails = event909?.eventDetails ?? []

  const preparedEvents = useMemo(
    () =>
      eventDetails.map((details) => {
        const matched = matchedLookup.get(details?.rapidEventId?.toString())
        return {
          ...details,
          __leagueName: details?.leagueName ?? details?.league ?? 'Unknown League',
          __matched: Boolean(matched),
          __matchedRecord: matched ?? null,
        }
      }),
    [eventDetails, matchedLookup],
  )

  const filteredEvents = useMemo(() => {
    const normalizedFilters = {
      home: searchFilters.home.trim().toLowerCase(),
      away: searchFilters.away.trim().toLowerCase(),
      league: searchFilters.league.trim().toLowerCase(),
    }

    return preparedEvents.filter((event) => {
      if (statusFilter.id === 'pending' && event.__matched) {
        return false
      }
      if (statusFilter.id === 'matched' && !event.__matched) {
        return false
      }

      const homeTeam = event.homeTeam?.toLowerCase() ?? ''
      const awayTeam = event.awayTeam?.toLowerCase() ?? ''
      const leagueName = event.__leagueName?.toLowerCase() ?? ''

      if (normalizedFilters.home && !homeTeam.includes(normalizedFilters.home)) {
        return false
      }
      if (normalizedFilters.away && !awayTeam.includes(normalizedFilters.away)) {
        return false
      }
      if (normalizedFilters.league && !leagueName.includes(normalizedFilters.league)) {
        return false
      }

      return true
    })
  }, [preparedEvents, searchFilters, statusFilter])

  const onFilterChange = (field) => (event) => {
    const value = event.target.value
    setSearchFilters((prev) => ({ ...prev, [field]: value }))
  }

  return (
    <div className="min-h-screen w-full bg-customGray pt-1 text-sm dark:bg-custom_dark_background">
      <header className="">
        <div className="mx-auto flex max-w-7xl flex-col gap-2 px-3 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-4 lg:px-6">
          <HeaderTitle title="Match Event (v2)" />
          <div className="flex flex-wrap items-center gap-2 text-xs font-semibold">
            <button
              type="button"
              onClick={handleAutoMatch}
              disabled={isAutoMatching || isLoading || !event555.length || !eventDetails.length}
              className="rounded-full border border-customAccent px-3 py-1.5 text-customAccent transition hover:bg-customAccent hover:text-white focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 dark:text-customLightYellow dark:hover:text-white"
            >
              {isAutoMatching ? 'Matching…' : '✨ Auto match direct teams'}
            </button>
            <span className="rounded-full bg-customAccent/10 px-3 py-1 text-customAccent dark:bg-customAccent/20 dark:text-customLightYellow">
              555Mix · {count555}
            </span>
            <span className="rounded-full bg-customVibrantGreen/10 px-3 py-1 text-customVibrantGreen dark:bg-customDarkGreen/30 dark:text-customLightYellow">
              Sport909 · {count909}
            </span>
          </div>
        </div>
      </header>
      <main>
        <div className="mx-auto max-w-7xl space-y-2 px-3 py-4 sm:px-4 lg:px-6">
          {isLoading && (
            <div className="flex justify-center py-8">
              <LoadingSpinner />
            </div>
          )}

          {!isLoading ? (
            <Fragment>
              <section className="rounded-xl border border-customAccentBlue/20 bg-customAccentBlue/10 p-3 backdrop-blur dark:border-white/10 dark:bg-glassDark">
                <h2 className="text-base font-semibold text-customPrimaryText dark:text-white">
                  Filter events
                </h2>
                <div className="mt-3 grid items-center gap-3 md:grid-cols-2 lg:grid-cols-4">
                  <div className="flex flex-col gap-1">
                    <label className="text-xs font-semibold uppercase tracking-wide text-customSecondaryText dark:text-customLightMilkWhite/70">
                      Home team
                    </label>
                    <input
                      type="text"
                      value={searchFilters.home}
                      onChange={onFilterChange('home')}
                      placeholder="Search by home team"
                      className="mt-1 rounded-lg border border-transparent bg-white/80 px-3 py-1.5 text-sm text-customPrimaryText outline-none ring-1 ring-customSlate/90 transition focus:border-customAccent/50 dark:bg-custom_dark_primary2 dark:text-white dark:ring-white/10"
                    />
                  </div>
                  <div className="flex flex-col gap-1">
                    <label className="text-xs font-semibold uppercase tracking-wide text-customSecondaryText dark:text-customLightMilkWhite/70">
                      Away team
                    </label>
                    <input
                      type="text"
                      value={searchFilters.away}
                      onChange={onFilterChange('away')}
                      placeholder="Search by away team"
                      className="mt-1 rounded-lg border border-transparent bg-white/80 px-3 py-1.5 text-sm text-customPrimaryText outline-none ring-1 ring-customSlate/90 transition focus:border-customAccent/50 dark:bg-custom_dark_primary2 dark:text-white dark:ring-white/10"
                    />
                  </div>
                  <div className="flex flex-col gap-1">
                    <label className="text-xs font-semibold uppercase tracking-wide text-customSecondaryText dark:text-customLightMilkWhite/70">
                      League
                    </label>
                    <input
                      type="text"
                      value={searchFilters.league}
                      onChange={onFilterChange('league')}
                      placeholder="Search by league"
                      className="mt-1 rounded-lg border border-transparent bg-white/80 px-3 py-1.5 text-sm text-customPrimaryText outline-none ring-1 ring-customSlate/90 transition focus:border-customAccent/50 dark:bg-custom_dark_primary2 dark:text-white dark:ring-white/10"
                    />
                  </div>
                  <div className="flex flex-col gap-1">
                    <span className="text-xs font-semibold uppercase tracking-wide text-customSecondaryText dark:text-customLightMilkWhite/70">
                      Status
                    </span>
                    <div className="flex flex-wrap gap-2">
                      {STATUS_OPTIONS.map((option) => {
                        const isActive = option.id === statusFilter.id
                        return (
                          <button
                            key={option.id}
                            type="button"
                            onClick={() => setStatusFilter(option)}
                            className={`text-sm transition focus:outline-none ${
                              isActive
                                ? 'font-semibold underline decoration-2 decoration-customAccent text-customAccent dark:text-customLightYellow'
                                : 'text-customSecondaryText hover:text-customAccent dark:text-customLightMilkWhite/70 dark:hover:text-customLightYellow'
                            }`}
                          >
                            {option.name}
                          </button>
                        )
                      })}
                    </div>
                  </div>
                </div>
              </section>

              {filteredEvents.length ? (
                <>
                <section className="hidden overflow-hidden rounded-lg border border-customBorder/20 bg-white dark:border-custom_dark_secondary dark:bg-custom_dark_primary2 md:block">
                  <div className="overflow-x-auto">
                    <table className="min-w-[980px] w-full border-collapse">
                      <thead className="sticky top-0 z-10">
                        <tr className="bg-gradient-to-r from-background via-background/90 to-background/80 text-customFontMilkWhite dark:from-custom_dark_background dark:via-custom_dark_primary dark:to-custom_dark_background">
                          <th className="w-[112px] px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-[0.16em]">Time</th>
                          <th className="min-w-[180px] px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-[0.16em]">League</th>
                          <th className="min-w-[240px] px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-[0.16em]">Sport909 Event</th>
                          <th className="min-w-[280px] px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-[0.16em]">555Mix Event</th>
                          <th className="w-[92px] px-3 py-2 text-center text-[11px] font-semibold uppercase tracking-[0.16em]">Status</th>
                          <th className="w-[64px] px-3 py-2 text-center text-[11px] font-semibold uppercase tracking-[0.16em]">Edit</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredEvents.map((details, rowIndex) => {
                      const matched = details.__matchedRecord
                      const pendingSelectionId =
                        pendingLookup.get(details?.rapidEventId?.toString()) ?? null
                      const effectiveEventId =
                        pendingSelectionId ?? matched?.eventId555 ?? null
                      return (
                          <EventSelect
                          key={details.rapidEventId ?? details.eventId ?? details.homeTeam}
                          event909={details}
                          leagueName={details.__leagueName}
                          event555={event555}
                          matched={matched}
                          matchId={matched?._id ?? null}
                          eventId555={effectiveEventId}
                          handleChange={handleChange}
                          getEvent={getEvent}
                          rowIndex={rowIndex}
                          />
                        )
                        })}
                      </tbody>
                    </table>
                  </div>

                </section>
                <section className="grid gap-2 md:hidden">
                  {filteredEvents.map((details, rowIndex) => {
                    const matched = details.__matchedRecord
                    const pendingSelectionId =
                      pendingLookup.get(details?.rapidEventId?.toString()) ?? null
                    const effectiveEventId =
                      pendingSelectionId ?? matched?.eventId555 ?? null
                    return (
                      <EventSelect
                        key={`mobile-${details.rapidEventId ?? details.eventId ?? details.homeTeam}`}
                        event909={details}
                        leagueName={details.__leagueName}
                        event555={event555}
                        matched={matched}
                        matchId={matched?._id ?? null}
                        eventId555={effectiveEventId}
                        handleChange={handleChange}
                        getEvent={getEvent}
                        rowIndex={rowIndex}
                        mobile
                      />
                    )
                  })}
                </section>
                </>
              ) : (
                <section className="flex min-h-[32vh] flex-col items-center justify-center rounded-2xl border border-dashed border-customSlate/50 bg-glassLight p-8 text-center dark:border-white/10 dark:bg-glassDark">
                  {/* <img
                    src="/nodata.svg"
                    width={120}
                    height={120}
                    alt="No data"
                    className="mb-4"
                  /> */}
                  <h3 className="text-base font-semibold text-customPrimaryText dark:text-white">
                    No events found
                  </h3>
                  <p className="max-w-md text-sm text-customSecondaryText dark:text-customLightMilkWhite/70">
                    Try adjusting your filters or sync events again to see more matches.
                  </p>
                </section>
              )}

              {eventDetails.length ? (
                <div className="flex flex-col items-center justify-between gap-2 rounded-xl border border-customAccentBlue/20 bg-glassLight p-3 sm:flex-row dark:border-white/10 dark:bg-glassDark">
                  <div className="text-xs text-customSecondaryText dark:text-customLightMilkWhite/70">
                    {selectedEventList.length
                      ? `${selectedEventList.length} pending selection${
                          selectedEventList.length > 1 ? 's' : ''
                        } ready to save`
                      : 'No pending selections yet'}
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      className="rounded-lg border border-transparent bg-customPredawn px-4 py-1.5 text-sm font-semibold text-customLightMilkWhite transition hover:bg-customPredawn/80 focus:outline-none dark:bg-customAccent dark:text-white dark:hover:bg-customAccent/80"
                      onClick={handleSave}
                    >
                      Save changes
                    </button>
                    <button
                      type="button"
                      className="rounded-lg border border-customSlate/50 bg-customSlate px-4 py-1.5 text-sm font-semibold text-customPrimaryText transition hover:border-slate-400 focus:outline-none dark:border-white/10 dark:bg-transparent dark:text-customLightMilkWhite dark:hover:border-customAccent dark:hover:text-customAccent"
                      onClick={handleReset}
                    >
                      Reset
                    </button>
                  </div>
                </div>
              ) : null}
              <ConfirmationDialog
                isOpen={isSaveConfirmOpen}
                setIsOpen={setIsSaveConfirmOpen}
                title="Confirm event matches"
                contextText={`Save ${selectedEventList.length} event match${selectedEventList.length === 1 ? '' : 'es'}? Please review the selected mappings before continuing.`}
                confirmLabel="Save changes"
                cancelLabel="Cancel"
                handleSubmit={handleSaveConfirmed}
                appearance="warning"
              />
            </Fragment>
          ) : null}
        </div>
      </main>
    </div>
  )
}

export default MatchEvent
